import { isSchemaFlagTrue, orderedPropertyEntries } from './registrySchemaUtils';

/**
 * Registry Entry Attribute Utilities
 *
 * Helpers for normalizing domain-registry entry attributes before they are
 * sent to the backend, based on the owning registry's JSON schema.
 */

/**
 * Coerces attribute values against the registry schema's declared property
 * types, so numeric fields reach the backend as real JS numbers rather than
 * the strings produced by `<TextField type="number">`.
 *
 * Only `type: 'number'` properties are touched:
 * - a non-empty string is converted to a Number (when it parses cleanly)
 * - '' / null / undefined are left as-is (absent/empty stays absent/empty)
 * - values that are already numbers are left as-is
 *
 * Booleans, dates/datetimes/times and any other type are never modified —
 * this function must stay a pure, submit-time normalization step (it is not
 * meant to run on every keystroke, which would break mid-typing decimals
 * such as "1." or "-").
 *
 * @param {Object} attributes - raw entry attributes (e.g. from form state)
 * @param {Object} schemaProperties - the registry schema's `properties` map
 * @returns {Object} a new attributes object with numeric fields coerced
 */
export const coerceEntryAttributesToSchema = (attributes, schemaProperties = {}) => {
  const source = attributes || {};
  const coerced = { ...source };

  Object.entries(schemaProperties || {}).forEach(([key, field]) => {
    if (!field || field.type !== 'number') {
      return;
    }
    const value = coerced[key];
    if (typeof value !== 'string') {
      return; // already a Number, boolean, undefined, etc. -> leave untouched
    }
    if (value.trim() === '') {
      return; // empty string -> leave as-is (treated as absent downstream)
    }
    const numericValue = Number(value);
    if (!Number.isNaN(numericValue)) {
      coerced[key] = numericValue;
    }
  });

  return coerced;
};

/**
 * Chooses which registry-schema properties to show as columns in the entries
 * ("Voci") table, and in what order.
 *
 * `visible_on_ui` is an *optional* curation flag. Seeded, imported and most
 * hand-authored schemas never set it, so gating columns on it alone leaves the
 * entries table with no data columns at all. This helper therefore:
 *  - shows only the flagged properties when the author has curated at least one, and
 *  - falls back to ALL declared properties otherwise,
 * so the table always mirrors the fields the entry form edits. Columns follow the
 * schema's authoring order (see orderedPropertyEntries: propertyOrder, else key first).
 *
 * @param {Object} schema - the registry schema (`properties` + optional `propertyOrder`)
 * @returns {Array<{key: string, label: string, type: string, isKey: boolean, field: Object}>}
 */
export const entryDisplayColumns = (schema) => {
  const entries = orderedPropertyEntries(schema);
  const curated = entries.filter(([, field]) => field && field.visible_on_ui);
  const chosen = curated.length > 0 ? curated : entries;
  return chosen.map(([key, field]) => {
    const f = field || {};
    return {
      key,
      label: f.name || key,
      type: f.type || 'string',
      isKey: isSchemaFlagTrue(f.key),
      field: f,
    };
  });
};

/**
 * Formats a single entry attribute value for read-only display in the entries
 * table. Keeps the transform intentionally small: booleans read as Italian
 * Sì/No, absent values render blank, everything else is shown verbatim.
 *
 * @param {Object} field - the schema property definition for this column
 * @param {*} value - the raw attribute value
 * @returns {string}
 */
export const formatEntryValue = (field, value) => {
  if (value === null || value === undefined) {
    return '';
  }
  if ((field && field.type === 'boolean') || typeof value === 'boolean') {
    return value ? 'Sì' : 'No';
  }
  return String(value);
};

export default coerceEntryAttributesToSchema;
